

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  TextField, Button, Typography, Paper, Grid, IconButton, AppBar, Box, styled,
  CircularProgress, Card, Alert, Snackbar, Divider, Stack, MenuItem,
  Toolbar, Dialog, DialogTitle, DialogContent, Container, Avatar, DialogActions, DialogContentText
} from '@mui/material';

import EXIF from 'exif-js';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import AccessRequestForm from './AssignUserDashboard';
import EventDetails from './EventDetails';
import { useTheme } from '@mui/material/styles';
import {
  Error as ErrorIcon,
  Cancel as CancelIcon,
  Logout as LogoutIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Share as ShareIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Notifications as NotificationsIcon,
  CloudUpload,
  VideoLibrary as VideoLibraryIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';

import DraftsList from './DraftsList';
import ProfilePanel from './ProfilePanel';
import NotificationsPanel from './NotificationsPanel';
import EventForm from './EventForm';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const IS_LOCAL = process.env.REACT_APP_IS_LOCAL === 'true';

/* ─── Global styles ───────────────────────────────────────────────────────── */
const GlobalStyle = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,600&family=DM+Sans:wght@300;400;500;600&family=DM+Serif+Display:ital@0;1&display=swap');

    * { box-sizing: border-box; margin: 0; padding: 0; }

    body {
      background: #FAF8F4;
      font-family: 'DM Sans', sans-serif;
      color: #1a1510;
      min-height: 100vh;
      overflow-x: hidden;
    }

    /* Warm paper texture via subtle gradient noise */
    body::before {
      content: '';
      position: fixed;
      inset: 0;
      background:
        radial-gradient(ellipse 70% 60% at 5% 0%, rgba(217,168,100,0.12) 0%, transparent 55%),
        radial-gradient(ellipse 50% 50% at 95% 100%, rgba(180,140,200,0.09) 0%, transparent 55%),
        radial-gradient(ellipse 60% 40% at 60% 30%, rgba(240,220,190,0.18) 0%, transparent 50%);
      pointer-events: none;
      z-index: 0;
    }

    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(22px); }
      to   { opacity: 1; transform: translateY(0); }
    }

    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50%       { transform: translateY(-6px); }
    }

    @keyframes shimmer {
      0%   { background-position: -600px 0; }
      100% { background-position: 600px 0; }
    }

    @keyframes pulse-warm {
      0%   { box-shadow: 0 0 0 0 rgba(194,130,60,0.4); }
      70%  { box-shadow: 0 0 0 10px rgba(194,130,60,0); }
      100% { box-shadow: 0 0 0 0 rgba(194,130,60,0); }
    }

    /* MUI overrides for light theme */
    .MuiDialog-paper {
      background: #FFFEFB !important;
      border: 1px solid rgba(194,130,60,0.18) !important;
      border-radius: 20px !important;
      color: #1a1510 !important;
      box-shadow: 0 32px 80px rgba(100,70,30,0.14), 0 2px 8px rgba(100,70,30,0.08) !important;
    }

    .MuiDialogTitle-root {
      color: #1a1510 !important;
      font-family: 'DM Serif Display', serif !important;
    }

    .MuiDialogContent-root {
      color: #4a3f30 !important;
    }

    .MuiTextField-root .MuiOutlinedInput-root {
      color: #1a1510 !important;
      background: rgba(250,248,244,0.8) !important;
      border-radius: 10px !important;
    }

    .MuiTextField-root .MuiOutlinedInput-root fieldset {
      border-color: rgba(194,130,60,0.25) !important;
    }

    .MuiTextField-root .MuiOutlinedInput-root:hover fieldset {
      border-color: rgba(194,130,60,0.55) !important;
    }

    .MuiTextField-root .MuiOutlinedInput-root.Mui-focused fieldset {
      border-color: #C2823C !important;
    }

    .MuiInputLabel-root {
      color: #8a7055 !important;
    }
    .MuiInputLabel-root.Mui-focused {
      color: #C2823C !important;
    }

    .MuiSelect-icon { color: #C2823C !important; }

    .MuiMenuItem-root {
      background: #FFFEFB !important;
      color: #1a1510 !important;
    }
    .MuiMenuItem-root:hover {
      background: rgba(194,130,60,0.09) !important;
    }

    .MuiDivider-root {
      border-color: rgba(194,130,60,0.14) !important;
    }

    .MuiCircularProgress-root { color: #C2823C !important; }

    .MuiSnackbar-root .MuiPaper-root {
      background: #FFFEFB !important;
      border: 1px solid rgba(194,130,60,0.25) !important;
      color: #1a1510 !important;
      box-shadow: 0 8px 32px rgba(100,70,30,0.15) !important;
    }

    ::-webkit-scrollbar { width: 5px; }
    ::-webkit-scrollbar-track { background: transparent; }
    ::-webkit-scrollbar-thumb {
      background: rgba(194,130,60,0.28);
      border-radius: 3px;
    }
    ::-webkit-scrollbar-thumb:hover {
      background: rgba(194,130,60,0.55);
    }
  `}</style>
);

/* ─── Design tokens ───────────────────────────────────────────────────────── */
const T = {
  amber:   '#C2823C',
  amberLight: '#E8A85A',
  sage:    '#6B8C6E',
  rose:    '#C4706A',
  slate:   '#5A6880',
  cream:   '#FAF8F4',
  ivory:   '#FFFEFB',
  paper:   '#F5F1EA',
  ink:     '#1a1510',
  inkMid:  '#4a3f30',
  inkLight:'#8a7055',
  border:  'rgba(194,130,60,0.18)',
  shadow:  '0 8px 40px rgba(100,70,30,0.12), 0 1px 4px rgba(100,70,30,0.06)',
  shadowHover: '0 20px 60px rgba(100,70,30,0.18), 0 4px 12px rgba(100,70,30,0.1)',
};

/* ─── Styled components ───────────────────────────────────────────────────── */
const NavButton = styled(Button)(() => ({
  fontFamily: "'DM Sans', sans-serif",
  fontWeight: 500,
  fontSize: '0.9rem',
  color: T.inkMid,
  textTransform: 'none',
  padding: '7px 18px',
  borderRadius: '50px',
  border: '1px solid transparent',
  letterSpacing: '0.01em',
  transition: 'all 0.22s ease',
  '&:hover': {
    color: T.amber,
    background: 'rgba(194,130,60,0.08)',
    border: `1px solid rgba(194,130,60,0.25)`,
  },
}));

const PrimaryButton = styled(Button)(() => ({
  fontFamily: "'DM Sans', sans-serif",
  fontWeight: 600,
  fontSize: '0.95rem',
  letterSpacing: '0.02em',
  textTransform: 'none',
  color: '#fff',
  background: 'linear-gradient(135deg, #C2823C 0%, #D4944E 100%)',
  borderRadius: '50px',
  padding: '13px 36px',
  boxShadow: '0 4px 20px rgba(194,130,60,0.38)',
  transition: 'all 0.28s cubic-bezier(0.34,1.56,0.64,1)',
  '&:hover': {
    transform: 'translateY(-2px) scale(1.02)',
    boxShadow: '0 10px 36px rgba(194,130,60,0.48)',
    background: 'linear-gradient(135deg, #D4944E 0%, #C2823C 100%)',
  },
  '&:active': { transform: 'translateY(0) scale(0.99)' },
}));

const GlassCard = styled(Box)(() => ({
  background: T.ivory,
  border: `1px solid ${T.border}`,
  borderRadius: '18px',
  overflow: 'hidden',
  transition: 'all 0.35s cubic-bezier(0.34,1.56,0.64,1)',
  position: 'relative',
  cursor: 'pointer',
  boxShadow: T.shadow,
  '&:hover': {
    transform: 'translateY(-8px) scale(1.015)',
    boxShadow: T.shadowHover,
    border: `1px solid rgba(194,130,60,0.38)`,
  },
  '&:hover .card-actions': { opacity: 1, transform: 'translateY(0)' },
  '&:hover .card-img': { transform: 'scale(1.06)' },
  '&:hover .card-overlay-top': { opacity: 1 },
}));

const IconBtn = styled(IconButton)(() => ({
  color: '#fff',
  background: 'rgba(255,255,255,0.22)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255,255,255,0.3)',
  width: 36, height: 36,
  transition: 'all 0.2s ease',
  '&:hover': {
    background: 'rgba(255,255,255,0.45)',
    transform: 'scale(1.12)',
  },
}));

const Tag = styled(Box)(({ color = T.amber }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '3px 11px',
  borderRadius: '50px',
  fontSize: '0.68rem',
  fontWeight: 600,
  letterSpacing: '0.09em',
  textTransform: 'uppercase',
  color: color,
  background: `${color}18`,
  border: `1px solid ${color}35`,
}));

/* ─── Helpers ─────────────────────────────────────────────────────────────── */
const getBasename = (path) => path.split('/').pop().split('\\').pop();

export const handleViewVideo = async (event, userId, setFullVideoUrl, setVideoModalVisible) => {
  try {
    const videos = event.videos;
    if (!videos || videos.length === 0) { window.alert('No video available.'); return; }
    const latestVideo = videos[videos.length - 1];
    const watermarkedVideo = videos.find(v => v.url.includes('watermarked')) || latestVideo;
    const basename = getBasename(watermarkedVideo.url);
    const eventName = event.eventName;
    if (IS_LOCAL) {
      let localVideoUrl = `${BASE_URL}/videos/${userId}/${eventName}/${basename}`;
      localVideoUrl += localVideoUrl.includes('?') ? `&t=${Date.now()}` : `?t=${Date.now()}`;
      setVideoModalVisible(false);
      setTimeout(() => { setFullVideoUrl(localVideoUrl); setVideoModalVisible(true); }, 100);
      return;
    }
    const apiUrl = `${process.env.REACT_APP_VIDEOS_API_URL}/${userId}/${eventName}/${basename}`;
    const response = await axios.get(apiUrl);
    if (response.data?.videoUrl) {
      let cloudFrontUrl = response.data.videoUrl;
      cloudFrontUrl += cloudFrontUrl.includes('?') ? `&t=${Date.now()}` : `?t=${Date.now()}`;
      setVideoModalVisible(false);
      setTimeout(() => { setFullVideoUrl(cloudFrontUrl); setVideoModalVisible(true); }, 100);
    } else window.alert('Failed to fetch video URL.');
  } catch (error) {
    console.error('Error fetching video URL:', error);
    window.alert('Error viewing video.');
  }
};

/* ─── Secure Video Dialog ─────────────────────────────────────────────────── */
const SecureVideoDialog = ({ videoModalVisible, setVideoModalVisible, fullVideoUrl, setFullVideoUrl }) => {
  const videoRef = useRef(null);
  const [securityAlert, setSecurityAlert] = useState(false);

  useEffect(() => {
    if (!videoModalVisible) return;
    const handleKeyDown = (e) => {
      if (e.key === 'PrintScreen' || e.keyCode === 44) { e.preventDefault(); setSecurityAlert('Screenshot prevented'); }
      if (e.ctrlKey && e.key === 's') { e.preventDefault(); setSecurityAlert('Save page prevented'); }
    };
    const handleContextMenu = (e) => { e.preventDefault(); setSecurityAlert('Right-click disabled'); };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('contextmenu', handleContextMenu);
    return () => { document.removeEventListener('keydown', handleKeyDown); document.removeEventListener('contextmenu', handleContextMenu); };
  }, [videoModalVisible]);

  const handleClose = () => { setVideoModalVisible(false); setFullVideoUrl(null); setSecurityAlert(false); };
  if (!videoModalVisible) return null;

  return (
    <Dialog open={videoModalVisible} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: T.rose, animation: 'pulse-warm 2s infinite' }} />
          <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontWeight: 400, color: T.ink, fontSize: '1.15rem' }}>
            Protected Playback
          </Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: T.inkLight, '&:hover': { color: T.rose, background: `${T.rose}12` } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        {securityAlert && (
          <Alert severity="warning" sx={{ mb: 2, background: `${T.amberLight}15`, border: `1px solid ${T.amberLight}40`, color: T.amber, borderRadius: '10px' }}>
            {securityAlert}
          </Alert>
        )}
        {fullVideoUrl ? (
          <Box sx={{ width: '100%', height: '60vh', borderRadius: '14px', overflow: 'hidden', border: `1px solid ${T.border}`, boxShadow: '0 4px 24px rgba(100,70,30,0.1)' }}>
            <video ref={videoRef} key={fullVideoUrl} controls controlsList="nodownload noremoteplayback"
              style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#111' }}
              onContextMenu={(e) => e.preventDefault()}>
              <source src={fullVideoUrl} type="video/mp4" />
            </video>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px' }}>
            <CircularProgress />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* ─── Stat Card ───────────────────────────────────────────────────────────── */
const StatCard = ({ value, label, color, icon }) => (
  <Box sx={{
    background: T.ivory,
    border: `1px solid ${T.border}`,
    borderRadius: '16px',
    px: 3, py: 2.5,
    textAlign: 'center',
    boxShadow: T.shadow,
    animation: 'fadeInUp 0.5s ease both',
    minWidth: 110,
    transition: 'all 0.25s ease',
    '&:hover': { boxShadow: T.shadowHover, transform: 'translateY(-3px)' },
  }}>
    <Typography sx={{
      fontFamily: "'Cormorant Garamond', serif",
      fontWeight: 700,
      fontSize: '2.2rem',
      color: color,
      lineHeight: 1,
      mb: 0.5,
    }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: '0.72rem', color: T.inkLight, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 500 }}>
      {label}
    </Typography>
  </Box>
);

/* ─── Main Dashboard ──────────────────────────────────────────────────────── */
const DashBoard = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [eventName, setEventName] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [eventLocation, setEventLocation] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [photos, setPhotos] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [customMusicFile, setCustomMusicFile] = useState(null);
  const [toBeDeletedImages, setToBeDeletedImages] = useState([]);

  const { userId, info } = location.state || {};
  const [userData, setUserData] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);

  const [events, setEvents] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [draftImages, setDraftImages] = useState({});
  const [eventImages, setEventImages] = useState({});
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [draftId, setDraftId] = useState(null);
  const [eventId, setEventId] = useState(null);

  const [accessRequests, setAccessRequests] = useState([]);
  const [showAccessRequestForm, setShowAccessRequestForm] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [videoId, setVideoId] = useState('');
  const [shareId, setShareId] = useState('');
  const [loadingShare, setLoadingShare] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const [activeTab, setActiveTab] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showEventDetails, setShowEventDetails] = useState(false);
  const [userid, setUserId] = useState('');
  const [loading, setLoading] = useState(false);
  const [saveDraftLoading, setSaveDraftLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogMessage, setDialogMessage] = useState('');
  const [loadingDialogOpen, setLoadingDialogOpen] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  const [videoModalVisible, setVideoModalVisible] = useState(false);
  const [fullVideoUrl, setFullVideoUrl] = useState(null);
  const [guestPhotosAdded, setGuestPhotosAdded] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('');

  const fileInputRef = useRef();

  const eventCreationApiUrl = process.env.REACT_APP_EVENT_CREATION_API;
  const generateVideoApiUrl = process.env.REACT_APP_GENERATE_VIDEO_API;
  const eventsApiPath = process.env.REACT_APP_EVENTS_API;
  const accessAssignedUsersApiPath = process.env.REACT_APP_ACCESS_ASSIGNED_USERS_API;
  const editEventApiPath = process.env.REACT_APP_EDIT_EVENTS_API;
  const profileDetailsApi = process.env.REACT_APP_PROFILE_DETAILS_API;
  const eventListPhotosPath = process.env.REACT_APP_LIST_PHOTOS_API;
  const eventUploadPhotosPath = process.env.REACT_APP_UPLOAD_PHOTOS_API;

  useEffect(() => {
    if (info?.user) setUserData({ name: info.user.name, email: info.user.email, mobileNumber: info.user.mobileNumber });
  }, [userId, info]);

  useEffect(() => { fetchEvents(); fetchDrafts(); fetchUserDetails(); }, [userId]);

  useEffect(() => {
  if (activeTab === 'notifications' && userId) {  
    fetch(`${process.env.REACT_APP_NOTIFICATION_API_URL}/${userId}`)
      .then(r => r.json())
      .then(d => setNotifications(Array.isArray(d.notifications) ? d.notifications : []))
      .catch(console.error);
  }
}, [activeTab, userId]);
  useEffect(() => { if (activeTab === 'drafts') fetchDrafts(); }, [activeTab]);

  useEffect(() => {
    const fetchAccessRequests = async () => {
      if (!userId) return;
      try {
        const response = await axios.get(`${accessAssignedUsersApiPath}/${userId}`);
        setAccessRequests(response.data); setActiveTab('eventdetails');
      } catch (err) { console.error(err); }
    };
    fetchAccessRequests();
  }, []);

  useEffect(() => {
    const fetchEventImages = async () => {
      if (!Array.isArray(events)) return;
      const imageMap = {};
      for (const event of events) {
        const img = await fetchImage({ userId: event.userId || userId, eventName: event.eventName });
        if (img) imageMap[event.eventId] = img;
      }
      setEventImages(imageMap);
    };
    fetchEventImages();
  }, [events]);

  useEffect(() => {
    const fetchDraftImages = async () => {
      const updatedImages = { ...draftImages };
      for (const draft of drafts) {
        if (!updatedImages[draft._id]) {
          // ✅ no extra args — fetchImage signature is ({ userId, eventName })
          const img = await fetchImage({ userId, eventName: draft.eventName });
          if (img) updatedImages[draft._id] = img;
        }
      }
      setDraftImages(updatedImages);
    };
    if (drafts.length > 0) fetchDraftImages();
  }, [drafts]);

  const fetchUserDetails = async () => {
    if (!userId) { setLoading(false); return; }
    try { const r = await axios.get(`${profileDetailsApi}/${userId}`); setProfile(r.data); }
    catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const fetchEvents = async () => {
    if (!userId) return;
    try {
      const r = await axios.get(`${eventsApiPath}/${userId}`);
      if (r.data.message) setEvents([]);
      else setEvents([...r.data].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
    } catch (err) { console.error(err); setDialogOpen(true); }
  };

  const fetchDrafts = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${process.env.REACT_APP_FETCH_DRAFT_DATA_API}`, { params: { userId } });
      if (r.status === 200) setDrafts([...r.data.drafts].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
    } catch (err) { alert(err.message || 'Failed to fetch drafts'); } finally { setLoading(false); }
  };

  const fetchImage = async ({ userId, eventName }) => {
    try {
      const r = await fetch(
        `${eventListPhotosPath}/${userId}/${encodeURIComponent(eventName)}`,
        { headers: { Accept: 'application/json' } }
      );
      if (!r.ok) {
        console.warn(`fetchImage: HTTP ${r.status} for ${userId}/${eventName}`);
        return null;
      }
  
      const data = await r.json();
      const images = data?.images;
      if (!Array.isArray(images) || images.length === 0) return null;
  
      const first = images[0];
      if (!first || typeof first !== 'string') return null;
  
      // S3 returns full https:// URLs — use directly
      if (first.startsWith('http')) return first;
  
      // Local returns relative path like "filename.jpg" or "guest_uploads/filename.jpg"
      // eventUploadPhotosPath is defined at the top of DashBoard — pass it in or use it from closure
      return `${eventUploadPhotosPath}/${userId}/${encodeURIComponent(eventName)}/${first}`;
    } catch (err) {
      console.error('fetchImage error:', userId, eventName, err);
      return null;
    }
  };

  const fetchVideoShareDetails = async (eventId, userId) => {
    try {
      setLoadingShare(true);
      const r = await fetch(`${BASE_URL}/event/${eventId}/video-share/${userId}`);
      if (r.ok) { const d = await r.json(); if (d.success) { setVideoId(d.video_id); setShareId(d.share_id); setQrCodeUrl(d.qr_code_url); } }
    } catch (err) { console.error(err); } finally { setLoadingShare(false); }
  };

  const handleFileChange = useCallback(async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    e.target.value = null;
    const existingFiles = new Map(selectedImages.map(img => [img.id, true]));
    const processed = await Promise.all(files.map(async (file) => {
      const fileId = `${file.name}-${file.lastModified}-${file.size}`;
      if (existingFiles.has(fileId)) return null;
      try {
        const exifData = await new Promise(res => EXIF.getData(file, function () { res(EXIF.getAllTags(this)); }));
        let dateTaken;
        if (exifData?.DateTimeOriginal) {
          const [datePart, timePart] = exifData.DateTimeOriginal.split(' ');
          const [y, m, d] = datePart.split(':');
          dateTaken = new Date(`${y}-${m}-${d}T${timePart}Z`);
        }
        if (!dateTaken || isNaN(dateTaken.getTime())) dateTaken = new Date(file.lastModified);
        if (isNaN(dateTaken.getTime())) dateTaken = new Date();
        return { file, preview: URL.createObjectURL(file), dateTaken: dateTaken.getTime(), fileName: file.name, id: fileId, isNew: true, fileType: file.type.startsWith('video') ? 'video' : 'image' };
      } catch { return { file, preview: URL.createObjectURL(file), dateTaken: Date.now(), fileName: file.name, id: fileId, isNew: true, error: true, fileType: 'image' }; }
    }));
    const newImages = processed.filter(Boolean);
    if (!newImages.length) return;
    setSelectedImages(prev => [...prev, ...newImages].sort((a, b) => a.dateTaken - b.dateTaken));
  }, [selectedImages]);

  const handleRemoveImage = (index) => {
    const img = selectedImages[index];
    if (img.isUploaded) setToBeDeletedImages(p => [...p, img]);
    setSelectedImages(p => p.filter((_, i) => i !== index));
    setPhotos(p => p.filter((_, i) => i !== index));
  };

  const handleCreateEvent = async () => {
  if (!eventName || !category || !eventDate) {
    setError('Event name, category, and date are required.');
    setDialogOpen(true);
    return;
  }
  setError(''); setSuccess('');
  const formData = new FormData();
  ['userId', 'draftId', 'eventName', 'category', 'description'].forEach(k => formData.append(k, eval(k)));
  formData.append('location', eventLocation); formData.append('eventDate', eventDate);
  if (customMusicFile) formData.append('custom_music', customMusicFile);
  selectedImages.sort((a, b) => a.dateTaken - b.dateTaken).forEach((img, i) => { formData.append('photos', img.file); formData.append('photoOrder[]', i); });
  try {
    setLoadingMessage(`Creating "${eventName}"…`);
    setLoadingDialogOpen(true);
    const r = await axios.post(eventCreationApiUrl, formData, { headers: { 'Content-Type': 'multipart/form-data' } });

    setEventId(r.data.eventId);
    setLoadingMessage(`Generating video for "${eventName}"…`);
    await handleGenerateVideo({ eventId: r.data.eventId, eventName });

    console.log("draftid ",draftId)

    // ✅ Clean up the draft now that it's a real event
    if (draftId) {
      try {
        await axios.delete(`${process.env.REACT_APP_DELETE_DRAFT_API}/${draftId}`);
      } catch (e) {
        console.warn('Draft cleanup failed (non-fatal):', e);
      }
      setDraftId(null);
    }
    await fetchDrafts();   // refresh drafts list so it disappears immediately

    setLoadingDialogOpen(false);

    const msg = r.data.merged
      ? `"${eventName}" already existed — your new photos and details were merged in and video regenerated.`
      : `"${eventName}" created and video generated!`;

    setDialogTitle('Success');
    setDialogMessage(msg);
    setOpenDialog(true);
  } catch (err) {
    setError(err.response?.data?.error || 'An error occurred');
    setLoadingDialogOpen(false);
    setDialogOpen(true);
  }
};

  const handleUpdateEvent = async () => {
    const formData = new FormData();
    formData.append('userId', userId); formData.append('eventName', eventName); formData.append('category', category);
    formData.append('description', description); formData.append('location', eventLocation);
    formData.append('eventDate', new Date(eventDate).toISOString());
    if (customMusicFile) formData.append('custom_music', customMusicFile);
    selectedImages.forEach(img => { if (img.isNew && img.file) formData.append('photos', img.file); });
    try {
      setLoadingMessage(`Updating "${eventName}"…`); setLoadingDialogOpen(true);
      const r = await axios.put(`${editEventApiPath}/${selectedEventId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (r.status === 200) {
        for (const img of toBeDeletedImages) {
          try {
            let url = img.preview;
            if (url.includes('/uploaded-photos/')) url = url.split('/uploaded-photos/')[1];
            else if (url.includes('cloudfront.net/')) url = url.split('cloudfront.net/')[1];
            await axios.delete(`${process.env.REACT_APP_DELETE_PHOTO_API}/${selectedEventId}`, { headers: { 'Content-Type': 'application/json' }, data: { url } });
          } catch {}
        }
        setToBeDeletedImages([]);
        setLoadingMessage(`Generating video for "${eventName}"…`);
        await handleGenerateVideo({ eventId: selectedEventId, eventName });
        setLoadingDialogOpen(false); setDialogTitle('Success'); setDialogMessage(`"${eventName}" updated!`);setGuestPhotosAdded(false);  setOpenDialog(true); fetchEvents();
      }
    } catch (err) { setError(err.response?.data?.error || 'Error during update.'); setLoadingDialogOpen(false); setDialogOpen(true); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { selectedEventId ? await handleUpdateEvent() : await handleCreateEvent(); }
    catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleGenerateVideo = async ({ eventId, eventName }) => {
    const formData = new FormData();
    formData.append('eventId', eventId); formData.append('userId', userId);
    formData.append('eventName', eventName); formData.append('category', category);
    if (customMusicFile) formData.append('custom_music', customMusicFile);
    try {
      const r = await axios.post(generateVideoApiUrl, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (r.data.success) { setDialogTitle('Success'); setDialogMessage('Video generated!'); setOpenDialog(true); setTimeout(() => window.location.reload(), 2000); }
      else { setDialogTitle('Failed'); setDialogMessage('Failed to generate video.'); setOpenDialog(true); setLoadingDialogOpen(false); }
    } catch { setDialogTitle('Error'); setDialogMessage('Error generating video.'); setOpenDialog(true); setLoadingDialogOpen(false); }
  };

  const handleDraftData = async (draft) => {
    if (!draft) return;
    setEventName(draft.eventName); setCategory(draft.category); setDescription(draft.description);
    setEventLocation(draft.location); setEventDate(draft.eventDate.split('T')[0]);
    setSelectedEventId(draft.eventId); setDraftId(draft.draftId);
    const previews = await Promise.all(draft.photos.map(async (photo) => {
      const filename = photo.url.split('/').pop();
      const fileUrl = `${process.env.REACT_APP_UPLOAD_PHOTOS_API}/${userId}/${draft.eventName}/${filename}`;
      try {
        const r = await fetch(fileUrl, { headers: { 'Accept': 'application/json' } });
        const ct = r.headers.get('content-type');
        if (r.ok && ct?.includes('application/json')) { const d = await r.json(); return { preview: d.photoUrl, isUploaded: true }; }
        if (r.ok && ct?.startsWith('image/')) return { preview: fileUrl, isUploaded: true };
      } catch { return null; }
    }));
    setSelectedImages(previews.filter(Boolean)); setPhotos(draft.photos);
  };

  // ✅ Add isAutoSave param — defaults to false (manual click = navigate away)
const handleSaveDraft = async (isAutoSave = false) => {
  try {
    const formData = new FormData();
    formData.append('userId', userId);
    if (draftId) formData.append('draftId', draftId);   // ✅ send it so backend updates instead of duplicating
    formData.append('eventName', eventName);
    formData.append('category', category);
    formData.append('description', description);
    formData.append('eventId', selectedEventId);
    formData.append('eventLocation', eventLocation);
    formData.append('eventDate', eventDate instanceof Date ? eventDate.toISOString() : eventDate);
    if (customMusicFile) formData.append('custom_music', customMusicFile);
    selectedImages.forEach(img => { if (!img.isUploaded && img.file) formData.append('files', img.file); });

    const r = await axios.post(`${process.env.REACT_APP_DRAFT_DATA_API}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });

    if (r.data?.status === 'Draft saved successfully!'|| r.data?.status === 'Draft updated successfully!') {
      if (r.data?.draftId && !draftId) {
        setDraftId(r.data.draftId);
      }
      setAutoSaveStatus('saved');
      setTimeout(() => setAutoSaveStatus(''), 3000);
      fetchEvents();
      if (!isAutoSave) setDialogOpen(false);
    }
  } catch (err) {
    setAutoSaveStatus('error');
    setErrorMessage(err.message || 'Failed to save draft.');
  }
};

const handleUpdateDraft = async (e, isAutoSave = false) => {
  e.preventDefault(); setError(null);
  const formData = new FormData();
  formData.append('userId', userId);
  formData.append('eventName', eventName);
  formData.append('category', category);
  formData.append('description', description);
  formData.append('eventLocation', eventLocation);
  formData.append('eventDate', new Date(eventDate).toISOString());
  if (customMusicFile) formData.append('custom_music', customMusicFile);
  selectedImages.forEach(img => { if (!img.isUploaded && img.file instanceof File) formData.append('photos', img.file); });

  try {
    const r = await axios.put(`${process.env.REACT_APP_UPDATE_DRAFT_DATA_API}/${draftId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });

    if (r.status === 200 && r.data.status === 'Draft updated successfully!') {
      setAutoSaveStatus('saved');
      setTimeout(() => setAutoSaveStatus(''), 3000);

      // delete removed images unchanged...
      for (const img of toBeDeletedImages) {
        try {
          let url = img.preview;
          if (url.includes('/uploaded-photos/')) url = url.split('/uploaded-photos/')[1];
          else if (url.includes('cloudfront.net/')) url = url.split('cloudfront.net/')[1];
          await axios.delete(`${process.env.REACT_APP_DELETE_PHOTO_API}/${draftId}`, { headers: { 'Content-Type': 'application/json' }, data: { url } });
        } catch {}
      }
      setToBeDeletedImages([]);
      fetchEvents();

      // ✅ Only navigate away on manual save
      if (!isAutoSave) {
        setDialogTitle('Success');
        setDialogMessage('Draft updated!');
        setOpenDialog(true);
        setDialogOpen(false);   // close the form dialog
      }
    }
  } catch (err) {
    setAutoSaveStatus('error');
    setError(err.response?.data?.message || 'Error updating draft.');
  } finally { setLoading(false); }
};

  const handleDeleteDraft = async (draftId) => {
    if (!window.confirm('Delete this draft?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_DELETE_DRAFT_API}/${draftId}`);
      setDrafts(p => p.filter(d => d.draftId !== draftId));
      alert('Deleted!'); setShowEventDetails(false); setSelectedEventId(null); setActiveTab('createdEvents'); fetchEvents();
    } catch { alert('Failed to delete draft.'); }
  };

  const getFileType = (url) => {
    const ext = url.split('.').pop().toLowerCase();
    if (['mp4','avi','mov','mkv','webm','flv','wmv'].includes(ext)) return 'video';
    if (['jpg','jpeg','png','gif','bmp','webp'].includes(ext)) return 'image';
    return 'unknown';
  };

  const handlePhotosAddedToEvent = async (eventId) => {
    const event = events.find(e => e.eventId === eventId);
    if (!event) return;
    await handleEditEventClick(event);   // pre-fills form + loads photos
    setGuestPhotosAdded(true);           // triggers the banner in EventForm
    setActiveTab('form');
    setDialogOpen(true);
  };


  const handleEditEventClick = async (event) => {
    setDialogOpen(true); setActiveTab('form'); setSelectedEvent(event);
    setEventName(event.eventName); setCategory(event.category); 
    setSelectedEventId(event.eventId);
    setDescription(event.description); setEventLocation(event.location); 
    setEventDate(event.eventDate.split('T')[0]);
  
    try {
      const previews = await Promise.all(event.photos.map(async (photo) => {
        
        // photo.url is stored as "userId/eventName/filename.jpg"
        // or "userId/eventName/guest_uploads/filename.jpg"
        // Use it directly — don't strip to just filename
        const fileUrl = `${process.env.REACT_APP_UPLOAD_PHOTOS_API}/${photo.url}`;
        const filename = photo.url.split('/').pop();
  
        try {
          const r = await fetch(fileUrl, { headers: { 'Accept': 'application/json' } });
          const ct = r.headers.get('content-type');
  
          if (r.ok && ct?.includes('application/json')) {
            const d = await r.json();
            return { 
              preview: d.photoUrl, 
              isUploaded: true, 
              fileType: getFileType(d.photoUrl), 
              fileName: filename, 
              id: `existing-${photo.url}`
            };
          }
          if (r.ok && (ct?.startsWith('image/') || ct?.startsWith('video/'))) {
            return { 
              preview: fileUrl, 
              isUploaded: true, 
              fileType: getFileType(fileUrl), 
              fileName: filename, 
              id: `existing-${photo.url}` 
            };
          }
        } catch { return null; }
      }));
      setSelectedImages(previews.filter(Boolean));
    } catch (err) { console.error(err); }
  };










  const handleDeleteClick = (eventId, e) => { e.stopPropagation(); setEventToDelete(eventId); setConfirmDeleteOpen(true); };

  const handleConfirmDelete = async () => {
    if (!eventToDelete) return;
    try {
      const r = await axios.delete(`${process.env.REACT_APP_DELETE_EVENT_API}/${eventToDelete}`);
      if (r.status === 200) { setDialogTitle('Success'); setDialogMessage('Event deleted successfully'); fetchEvents(); }
    } catch { setDialogTitle('Error'); setDialogMessage('Failed to delete event'); }
    finally { setConfirmDeleteOpen(false); setEventToDelete(null); setResultDialogOpen(true); }
  };

  const handleShareEventClick = async (event, userId) => {
    setDialogOpen(true); setActiveTab('accessrequest');
    setSelectedEventId(event.eventId); setSelectedId(event._id); setShowAccessRequestForm(true);
    setVideoId(null); setShareId(null); setQrCodeUrl(null);
    await fetchVideoShareDetails(event.eventId, userId);
  };

  const closeEventDetails = () => { setShowEventDetails(false); setSelectedEvent({}); setSelectedEventId(null); setUserId(''); };
  const handleClose = () => { setShowAccessRequestForm(false); setSelectedEventId(null); setSelectedId(null); setDialogOpen(false); };
  const resetForm = () => { setEventName(''); setCategory(''); setDescription(''); setEventLocation(''); setEventDate(new Date()); setSelectedImages([]); setDraftId(null); setUserId(null); };

  const logoutUser = async () => {
    if (isLoggingOut) return;
    try {
      setIsLoggingOut(true);
      let token = localStorage.getItem('authToken');
      if (!token) throw new Error('No token found');
      await axios.post(`${process.env.REACT_APP_LOGOUT_API}`, {}, { headers: { Authorization: token.trim() } });
      localStorage.removeItem('authToken'); localStorage.removeItem('activeTab'); localStorage.removeItem('activePage');
      navigate('/login');
    } catch (err) { alert(err.response?.data?.error || 'Logout failed.'); }
    finally { setIsLoggingOut(false); setOpenLogoutDialog(false); }
  };

  const tabLabels = { drafts: 'Drafts', profile: 'Profile', form: selectedEventId ? 'Edit Memory' : 'New Memory', eventdetails: 'Event Details', accessrequest: 'Share Memory', notifications: 'Notifications' };

  /* ─── Render ─────────────────────────────────────────────────────────────── */
  return (
    <>
      <GlobalStyle />

      {/* ── Navigation Bar ── */}
      <Box component="nav" sx={{
        position: 'sticky', top: 0, zIndex: 100,
        background: 'rgba(250,248,244,0.88)',
        backdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${T.border}`,
        boxShadow: '0 1px 20px rgba(100,70,30,0.07)',
      }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', px: 4, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 34, height: 34, borderRadius: '10px',
              background: 'linear-gradient(135deg, #C2823C, #D4944E)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 14px rgba(194,130,60,0.35)',
            }}>
              <VideoLibraryIcon sx={{ fontSize: 16, color: '#fff' }} />
            </Box>
            <Typography sx={{
              fontFamily: "'Cormorant Garamond', serif",
              fontWeight: 700,
              fontSize: '1.6rem',
              color: T.ink,
              letterSpacing: '-0.01em',
              lineHeight: 1,
            }}>
              vimoir
            </Typography>
          </Box>

          {/* Nav links */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <NavButton onClick={() => { setActiveTab('drafts'); setDialogOpen(true); }}>Drafts</NavButton>
            <NavButton onClick={() => { setActiveTab('profile'); setDialogOpen(true); }}>Profile</NavButton>
            <IconButton onClick={() => { setActiveTab('notifications'); setDialogOpen(true); }}
              sx={{ color: T.inkLight, mx: 0.5, '&:hover': { color: T.amber, background: `${T.amber}12` } }}>
              <NotificationsIcon fontSize="small" />
            </IconButton>
            {/* Avatar placeholder */}
            <Box sx={{
              width: 32, height: 32, borderRadius: '50%',
              background: `linear-gradient(135deg, ${T.amber}33, ${T.amberLight}44)`,
              border: `1.5px solid ${T.amber}55`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', mx: 0.5,
              fontSize: '0.7rem', fontWeight: 700, color: T.amber,
            }}
              onClick={() => { setActiveTab('profile'); setDialogOpen(true); }}
            >
              {userData?.name?.[0]?.toUpperCase() || 'U'}
            </Box>
            <Button
              onClick={() => setOpenLogoutDialog(true)}
              startIcon={<LogoutIcon sx={{ fontSize: '0.85rem !important' }} />}
              sx={{
                color: T.inkLight, fontSize: '0.82rem', textTransform: 'none', fontFamily: "'DM Sans'",
                px: 1.5, py: 0.7, borderRadius: '8px', fontWeight: 500,
                '&:hover': { color: T.rose, background: `${T.rose}10` },
              }}
            >
              Sign out
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ── Logout Dialog ── */}
      <Dialog open={openLogoutDialog} onClose={() => setOpenLogoutDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: "'DM Serif Display', serif", fontWeight: 400 }}>Sign out?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: T.inkLight }}>You'll need to sign in again to access your memories.</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button onClick={() => setOpenLogoutDialog(false)} sx={{ color: T.inkLight, borderRadius: '50px', border: `1px solid ${T.border}`, px: 3, textTransform: 'none', fontFamily: "'DM Sans'" }}>
            Cancel
          </Button>
          <Button onClick={logoutUser} disabled={isLoggingOut}
            sx={{ background: 'linear-gradient(135deg, #C4706A, #b55e58)', color: '#fff', borderRadius: '50px', px: 3, textTransform: 'none', fontFamily: "'DM Sans'", '&:hover': { opacity: 0.9 } }}>
            {isLoggingOut ? 'Signing out…' : 'Sign out'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Hero Section ── */}
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: 4, pt: { xs: 8, md: 14 }, pb: 8, position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { md: 'center' }, gap: { xs: 6, md: 10 } }}>
          {/* Left: text */}
          <Box sx={{ flex: 1, animation: 'fadeInUp 0.7s ease both' }}>
            {/* Eyebrow label */}
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mb: 3, px: 2, py: 0.8, borderRadius: '50px', background: `${T.amber}14`, border: `1px solid ${T.amber}30` }}>
              <AutoAwesomeIcon sx={{ fontSize: 13, color: T.amber }} />
              <Typography sx={{ fontSize: '0.72rem', color: T.amber, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                AI-Powered Memories
              </Typography>
            </Box>

            <Typography sx={{
              fontFamily: "'DM Serif Display', serif",
              fontWeight: 400,
              fontStyle: 'italic',
              fontSize: { xs: '2.8rem', md: '3.8rem', lg: '4.8rem' },
              lineHeight: 1.08,
              color: T.ink,
              letterSpacing: '-0.02em',
              mb: 2,
            }}>
              Let the past<br />
              <Box component="span" sx={{ color: T.amber }}>play beautifully.</Box>
            </Typography>

            <Typography sx={{ fontSize: '1.05rem', color: T.inkLight, maxWidth: 420, lineHeight: 1.75, mb: 5, fontWeight: 300 }}>
              Transform your photos into cinematic memories — no editing skills required. Just your imagination.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <PrimaryButton onClick={() => { setActiveTab('form'); setSelectedEventId(null); resetForm();setGuestPhotosAdded(false);   setDialogOpen(true); }}>
                ✦ Create a Memory
              </PrimaryButton>
              {/* {events.length > 0 && (
                <Button sx={{
                  color: T.inkMid, border: `1px solid ${T.border}`, borderRadius: '50px',
                  px: 3.5, py: 1.6, fontFamily: "'DM Sans'", fontWeight: 500, textTransform: 'none',
                  background: T.ivory,
                  '&:hover': { background: T.paper, borderColor: `${T.amber}55`, color: T.amber },
                }}>
                  View Gallery ↓
                </Button>
              )} */}
            </Box>
          </Box>

          {/* Right: stat cards */}
          {events.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, animation: 'fadeInUp 0.8s ease 0.1s both', flexShrink: 0 }}>
              <StatCard value={events.length} label="Memories" color={T.amber} />
              <StatCard value={drafts.length} label="Drafts" color={T.sage} />
              <StatCard value={(notifications || []).length} label="Updates" color={T.slate} />
            </Box>
          )}
        </Box>

        {/* Decorative divider */}
        <Box sx={{ mt: { xs: 8, md: 10 }, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ flex: 1, height: '1px', background: `linear-gradient(90deg, transparent, ${T.border}, transparent)` }} />
          <Typography sx={{ fontSize: '0.7rem', color: T.inkLight, letterSpacing: '0.15em', textTransform: 'uppercase', fontWeight: 500 }}>
            Your Memories
          </Typography>
          <Box sx={{ flex: 1, height: '1px', background: `linear-gradient(90deg, transparent, ${T.border}, transparent)` }} />
        </Box>
      </Box>

      {/* ── Events Grid ── */}
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: 4, pb: 14, position: 'relative', zIndex: 1 }}>
        <Grid container spacing={3}>
          {Array.isArray(events) && events.length > 0 ? events.map((event, idx) => (
            <Grid item xs={12} sm={6} md={4} key={event._id || idx}>
              <GlassCard
                onClick={() => handleViewVideo(event, event.userId, setFullVideoUrl, setVideoModalVisible)}
                sx={{ height: 310, animation: `fadeInUp 0.5s ease ${idx * 0.07}s both` }}
              >
                {eventImages[event.eventId] ? (
                  <>
                    <Box className="card-img" sx={{
                      position: 'absolute', inset: 0,
                      backgroundImage: `url(${eventImages[event.eventId]})`,
                      backgroundSize: 'cover', backgroundPosition: 'center',
                      transition: 'transform 0.55s cubic-bezier(0.34,1.56,0.64,1)',
                    }} />

                    {/* Warm gradient overlay */}
                    <Box sx={{
                      position: 'absolute', inset: 0,
                      background: 'linear-gradient(to top, rgba(26,21,16,0.72) 0%, rgba(26,21,16,0.12) 50%, transparent 100%)',
                      zIndex: 2,
                    }} />

                    {/* Top badge on hover */}
                    <Box className="card-overlay-top" sx={{
                      position: 'absolute', top: 0, left: 0, right: 0, p: 1.8, display: 'flex',
                      justifyContent: 'space-between', alignItems: 'flex-start', zIndex: 3,
                      opacity: 0, transition: 'opacity 0.3s ease',
                    }}>
                      <Tag color="#fff">{event.category || 'Memory'}</Tag>
                    </Box>

                    {/* Bottom info */}
                    <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 2.5, zIndex: 3 }}>
                      <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontWeight: 400, fontSize: '1.1rem', color: '#fff', mb: 0.4, lineHeight: 1.2 }}>
                        {event.eventName || 'Untitled'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.73rem', color: 'rgba(255,255,255,0.55)', letterSpacing: '0.05em' }}>
                        {event.eventDate ? new Date(event.eventDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}
                      </Typography>
                    </Box>

                    {/* Action icons */}
                    <Box className="card-actions" sx={{
                      position: 'absolute', bottom: 18, right: 18, display: 'flex', gap: 0.8, zIndex: 4,
                      opacity: 0, transform: 'translateY(10px)', transition: 'all 0.3s ease',
                    }}>
                      {[
                        { icon: <InfoIcon sx={{ fontSize: 15 }} />, action: (e) => { e.stopPropagation(); setSelectedEventId(event.eventId); setSelectedEvent(event); setUserId(event.userId || ''); setShowEventDetails(true); setActiveTab('eventdetails'); setDialogOpen(true); } },
                        { icon: <EditIcon sx={{ fontSize: 15 }} />, action: (e) => { e.stopPropagation(); handleEditEventClick(event); } },
                        { icon: <DeleteIcon sx={{ fontSize: 15 }} />, action: (e) => handleDeleteClick(event.eventId, e) },
                        { icon: <ShareIcon sx={{ fontSize: 15 }} />, action: (e) => { e.stopPropagation(); handleShareEventClick(event, userId); } },
                      ].map(({ icon, action }, i) => (
                        <IconBtn key={i} onClick={action}>
                          {icon}
                        </IconBtn>
                      ))}
                    </Box>
                  </>
                ) : (
                  <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, zIndex: 2, position: 'relative', background: T.paper }}>
                    <Box sx={{
                      width: 52, height: 52, borderRadius: '14px',
                      background: `${T.amber}14`, border: `1px solid ${T.amber}25`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <VideoLibraryIcon sx={{ fontSize: 22, color: T.amber }} />
                    </Box>
                    <Typography sx={{ fontSize: '0.8rem', color: T.inkLight }}>Loading…</Typography>
                  </Box>
                )}
              </GlassCard>
            </Grid>
          )) : (
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', py: 12, animation: 'fadeInUp 0.6s ease' }}>
                {/* Decorative ring */}
                <Box sx={{
                  width: 120, height: 120, borderRadius: '50%', mx: 'auto', mb: 4,
                  background: `radial-gradient(circle, ${T.amber}10, ${T.amber}04)`,
                  border: `1px dashed ${T.amber}35`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  animation: 'float 4s ease-in-out infinite',
                }}>
                  <Box sx={{
                    width: 80, height: 80, borderRadius: '50%',
                    background: `${T.amber}12`,
                    border: `1px solid ${T.amber}30`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <VideoLibraryIcon sx={{ fontSize: 32, color: T.amber }} />
                  </Box>
                </Box>
                <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontWeight: 400, fontStyle: 'italic', fontSize: '1.6rem', color: T.inkMid, mb: 1.5 }}>
                  No memories yet
                </Typography>
                <Typography sx={{ color: T.inkLight, mb: 4, fontSize: '0.95rem', fontWeight: 300 }}>
                  Create your first video memory to get started
                </Typography>
                <PrimaryButton onClick={() => { setActiveTab('form'); setSelectedEventId(null); resetForm(); setDialogOpen(true); }}>
                  ✦ Create First Memory
                </PrimaryButton>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* ── Confirm Delete Dialog ── */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: "'DM Serif Display', serif", fontWeight: 400 }}>Delete this memory?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: T.inkLight }}>
            This action cannot be undone. The event and its videos will be permanently removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button onClick={() => setConfirmDeleteOpen(false)} sx={{ color: T.inkLight, border: `1px solid ${T.border}`, borderRadius: '50px', px: 3, textTransform: 'none', fontFamily: "'DM Sans'" }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} sx={{ background: 'linear-gradient(135deg, #C4706A, #b55e58)', color: '#fff', borderRadius: '50px', px: 3, textTransform: 'none', fontFamily: "'DM Sans'", '&:hover': { opacity: 0.9 } }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Result Dialog ── */}
      <Dialog open={resultDialogOpen} onClose={() => setResultDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textAlign: 'center', fontFamily: "'DM Serif Display', serif", fontWeight: 400, color: dialogTitle === 'Error' ? T.rose : T.sage }}>
          {dialogTitle === 'Success' ? '✓ ' : '✗ '}{dialogTitle}
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 1 }}>
          <Typography sx={{ color: T.inkMid }}>{dialogMessage}</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', p: 3 }}>
          <Button onClick={() => setResultDialogOpen(false)} sx={{
            background: dialogTitle === 'Error' ? 'linear-gradient(135deg, #C4706A, #b55e58)' : 'linear-gradient(135deg, #6B8C6E, #5a7b5d)',
            color: '#fff', borderRadius: '50px', px: 4, textTransform: 'none', fontFamily: "'DM Sans'",
          }}>OK</Button>
        </DialogActions>
      </Dialog>

      {/* ── Main Tabs Dialog ── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md"
        PaperProps={{ sx: { maxHeight: '90vh' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1.5, borderBottom: `1px solid ${T.border}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 3, height: 22, borderRadius: '2px', background: `linear-gradient(180deg, ${T.amber}, ${T.amberLight})` }} />
            <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontWeight: 400, fontSize: '1.3rem', color: T.ink }}>
              {tabLabels[activeTab] || ''}
            </Typography>
          </Box>
          <IconButton onClick={() => setDialogOpen(false)} sx={{ color: T.inkLight, '&:hover': { color: T.rose, background: `${T.rose}10` } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ borderColor: T.border, background: T.cream }}>
          {activeTab === 'form' && (
            <EventForm
              userId={userId} selectedEventId={selectedEventId} draftId={draftId}
              eventName={eventName} setEventName={setEventName}
              category={category} setCategory={setCategory}
              description={description} setDescription={setDescription}
              eventLocation={eventLocation} setEventLocation={setEventLocation}
              eventDate={eventDate} setEventDate={setEventDate}
              selectedImages={selectedImages}
              customMusicFile={customMusicFile} setCustomMusicFile={setCustomMusicFile}
              onFileChange={handleFileChange} onRemoveImage={handleRemoveImage}
              onSubmit={handleSubmit} onSaveDraft={handleSaveDraft} onUpdateDraft={handleUpdateDraft}
              loading={loading} saveDraftLoading={saveDraftLoading} error={error} success={success}
              snackbarOpen={snackbarOpen} onCloseSnackbar={() => setSnackbarOpen(false)}
              openDialog={openDialog} setOpenDialog={setOpenDialog}
              dialogTitle={dialogTitle} dialogMessage={dialogMessage}
              loadingDialogOpen={loadingDialogOpen} loadingMessage={loadingMessage}
              guestPhotosAdded={guestPhotosAdded}  
              autoSaveStatus={autoSaveStatus}   
              setAutoSaveStatus={setAutoSaveStatus}
            />
          )}

          {activeTab === 'profile' && <ProfilePanel userId={userId} profile={profile} setProfile={setProfile} />}

          {activeTab === 'drafts' && (
            <DraftsList
              drafts={drafts} draftImages={draftImages} loading={loading}
              onEditDraft={(item) => { setActiveTab('form'); handleDraftData(item); }}
              onDeleteDraft={handleDeleteDraft}
            />
          )}

          {activeTab === 'notifications' && (
            <NotificationsPanel
              notifications={notifications || []} events={events} drafts={drafts}
              eventImages={eventImages} draftImages={draftImages} userId={userId}
              onClickEvent={(event, userId) => handleViewVideo(event, userId, setFullVideoUrl, setVideoModalVisible)}
            />
          )}

          {activeTab === 'accessrequest' && selectedEventId && selectedId && (
            <Box sx={{ mt: 2 }}>
              <AccessRequestForm
                eventId={selectedEventId} requesterId={selectedId} qrCodeUrl={qrCodeUrl}
                userId={userId} videoId={videoId} shareId={shareId} handleClose={handleClose}
                eventName={selectedEvent?.eventName}  
                onPhotosAdded={handlePhotosAddedToEvent} 
              />
            </Box>
          )}

          {activeTab === 'eventdetails' && showEventDetails && selectedEventId && (
            <EventDetails eventId={selectedEventId} userId={userId} event={selectedEvent} onClose={closeEventDetails} />
          )}
        </DialogContent>
      </Dialog>

      {/* ── Video Player ── */}
      <SecureVideoDialog
        videoModalVisible={videoModalVisible} setVideoModalVisible={setVideoModalVisible}
        fullVideoUrl={fullVideoUrl} setFullVideoUrl={setFullVideoUrl}
      />

      {/* ── Footer ── */}
      <Box sx={{ borderTop: `1px solid ${T.border}`, py: 4, textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <Typography sx={{ fontSize: '0.78rem', color: T.inkLight, letterSpacing: '0.08em' }}>
          © {new Date().getFullYear()} vimoir — made with care
        </Typography>
      </Box>
    </>
  );
};

export default DashBoard;